import React, { useMemo, useState, useEffect, useRef } from 'react'
import { Card, message, Modal } from 'antd'
import { useNavigate } from 'react-router-dom'
import {
  rateDetailsListRequest,
  rateDetailsUpdateRequest,
  rateDetailsUpdateSortOrderRequest,
} from '@/AppRedux/actions/admin/rateDetailConfig'
import { RateData } from '@/AppRedux/actions/admin/rateDetailConfig/rateDetailConfigType'
import { useAppDispatch, useAppSelector } from '@/AppRedux/store/hooks'
import { useResponsive } from '@/hooks/useResponsive'
import { FlattenedRateData } from './components/types'
import { useSortOrder } from '@/hooks/useSortOrder'
import RateSectionHeader from './components/RateSectionHeader'
import RateDesktopTable from './components/RateDesktopTable'
import RateTabletTable from './components/RateTabletTable'
import RateMobileList from './components/RateMobileList'
import SortOrderActionBar from './components/SortOrderActionBar'
import UnsavedRatesModal from './components/UnsavedRatesModal'

const RateList: React.FC = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const { dataSource, loading, process } = useAppSelector((state) => state.rateDetailConfig)
  const rates = dataSource || []

  const [updatingRateCode, setUpdatingRateCode] = useState<string | null>(null)
  const [unsavedModalOpen, setUnsavedModalOpen] = useState(false)

  const useCurrentFirstRender = useRef(true)

  const { isMobile, isTablet, isDesktop } = useResponsive()

  useEffect(() => {
    if (useCurrentFirstRender.current) {
      dispatch(rateDetailsListRequest())
      useCurrentFirstRender.current = false
    }
  }, [dispatch])

  const flattenedRates: FlattenedRateData[] = useMemo(() => {
    return rates
      .map((rate) => {
        const defaultContent = rate.contents[0] || {}
        return { ...rate, ...defaultContent }
      })
      .filter((rate) => rate.contents && rate.contents.length > 0)
  }, [rates])

  const {
    isSortMode,
    isDirty,
    displayItems: displayRates,
    unsavedItems: unsavedRates,
    computeDiff,
    enterSortMode,
    cancelSortMode,
    handleDragEnd,
    handleMoveUp,
    handleMoveDown,
  } = useSortOrder(flattenedRates, (r) => r.rate_code)

  useEffect(() => {
    if (process.action === 'RATE_DETAILS_UPDATE_REQUEST') {
      if (process.status) {
        message.success('Rate updated successfully!')
        dispatch(rateDetailsListRequest())
      } else {
        message.error(process.message || 'Failed to update rate.')
      }
      setUpdatingRateCode(null)
    }
  }, [process, dispatch])

  useEffect(() => {
    if (process.action === 'RATE_DETAILS_UPDATE_SORT_ORDER_REQUEST') {
      if (process.status) {
        message.success('Sort order updated successfully!')
        dispatch(rateDetailsListRequest())
        cancelSortMode()
      } else {
        Modal.error({ title: 'Error', content: process.message || 'Failed to update sort order.' })
      }
    }
  }, [process, dispatch, cancelSortMode])

  const findRateByCode = (rateCode: string) =>
    rates.find((rate) => rate.rate_code === rateCode) || null

  const handleToggleRateEnabled = (record: FlattenedRateData, checked: boolean) => {
    const originalRate = findRateByCode(record.rate_code)
    if (!originalRate) {
      message.error('Could not find rate data to update.')
      return
    }
    setUpdatingRateCode(record.rate_code)
    const updatedRateData: RateData = { ...originalRate, enabled: checked }
    dispatch(rateDetailsUpdateRequest(updatedRateData))
  }

  const handleEditRate = (rateCode: string) => {
    navigate(`./${encodeURIComponent(rateCode)}/edit`)
  }

  const handleCreateRate = () => {
    navigate(`./create`)
  }

  const handleToggleSortMode = () => {
    if (isSortMode) {
      cancelSortMode()
      return
    }
    if (unsavedRates.length > 0) {
      setUnsavedModalOpen(true)
      return
    }
    enterSortMode()
  }

  const handleSaveSortOrder = () => {
    const diff = computeDiff()
    if (diff.length === 0) return
    dispatch(rateDetailsUpdateSortOrderRequest(
      diff.map((d) => ({ rate_id: d.id, sort_order: d.sort_order }))
    ))
  }

  const isSortSaving =
    loading && (process.action as string) === 'RATE_DETAILS_UPDATE_SORT_ORDER_REQUEST'

  const commonProps = {
    rates: displayRates,
    loading: loading && !process.status,
    updatingRateCode,
    isSortMode,
    onEdit: handleEditRate,
    onToggleEnabled: handleToggleRateEnabled,
    onDragEnd: handleDragEnd,
  }

  return (
    <Card>
      <RateSectionHeader
        onCreateRate={handleCreateRate}
        isSortMode={isSortMode}
        hasUnsavedRates={unsavedRates.length > 0}
        onToggleSortMode={handleToggleSortMode}
      />

      {isDesktop && <RateDesktopTable {...commonProps} />}
      {isTablet && !isDesktop && <RateTabletTable {...commonProps} />}
      {isMobile && (
        <RateMobileList
          rates={displayRates}
          loading={loading && !process.status}
          updatingRateCode={updatingRateCode}
          isSortMode={isSortMode}
          onEdit={handleEditRate}
          onToggleEnabled={handleToggleRateEnabled}
          onMoveUp={handleMoveUp}
          onMoveDown={handleMoveDown}
        />
      )}

      {isSortMode && (
        <SortOrderActionBar
          isDirty={isDirty}
          loading={isSortSaving}
          isMobile={isMobile}
          onCancel={cancelSortMode}
          onSave={handleSaveSortOrder}
        />
      )}

      <UnsavedRatesModal
        open={unsavedModalOpen}
        unsavedRates={unsavedRates}
        onClose={() => setUnsavedModalOpen(false)}
      />
    </Card>
  )
}

export default RateList
